"use client";
import { useEffect, useState } from "react";
import {
  addMonths,
  subMonths,
  addYears,
  subYears,
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  getYear,
  getDay,
} from "date-fns";
import icon from "../icons/Icons";
import { useIsMobile } from "@/utils/useIsMobile";

const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

type View = "day" | "month" | "year";

interface CustomCalendarProps {
  minSelectableDate?: Date;
  maxSelectableDate?: Date;
  fontSizeBase?: boolean;
  defaultSelectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  disableWeekends?: boolean;
  mobileCondition?: number;
  smallCalendar?: boolean;
  singleLetterDays?: boolean;
}

export default function CustomCalendar({
  minSelectableDate = new Date(),
  maxSelectableDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 10),
  ),
  fontSizeBase = true,
  defaultSelectedDate,
  onDateSelect,
  disableWeekends = false,
  singleLetterDays = false,
  mobileCondition = 631,
  smallCalendar = true,
}: CustomCalendarProps) {
  const [view, setView] = useState<View>("day");
  const [currentDate, setCurrentDate] = useState(
    startOfMonth(defaultSelectedDate || new Date()),
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultSelectedDate || null,
  );

  const year = getYear(currentDate);

  const isMobile = useIsMobile(mobileCondition);

  useEffect(() => {
    if (defaultSelectedDate) {
      setCurrentDate(startOfMonth(defaultSelectedDate));
    }
  }, [defaultSelectedDate]);

  const handlePrev = () => {
    if (view === "year") {
      setCurrentDate((prev) => subYears(prev, 12));
    } else if (view === "month") {
      setCurrentDate((prev) => subYears(prev, 1));
    } else {
      const prevMonth = subMonths(currentDate, 1);
      if (!isBefore(prevMonth, startOfMonth(minSelectableDate))) {
        setCurrentDate(prevMonth);
      }
    }
  };

  const handleNext = () => {
    if (view === "year") {
      setCurrentDate((prev) => addYears(prev, 12));
    } else if (view === "month") {
      setCurrentDate((prev) => addYears(prev, 1));
    } else {
      const nextMonth = addMonths(currentDate, 1);
      if (!isAfter(nextMonth, startOfMonth(maxSelectableDate))) {
        setCurrentDate(nextMonth);
      }
    }
  };

  const handleDateSelect = (day: Date) => {
    setSelectedDate(day);
    if (onDateSelect) onDateSelect(day);
  };

  const years = Array.from({ length: 12 }, (_, i) => year - 6 + i);
  const months = Array.from({ length: 12 }, (_, i) => i);

  const renderDayView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return (
      <>
        <div
          className={`grid grid-cols-7 text-center text-primaryBlack ${fontSizeBase ? "py-2 text-2xs" : "py-1.5 text-xs"} font-semibold border-y border-[#20202033] mb-2`}
        >
          {DAYS.map((day) => (
            <div key={day}>
              {singleLetterDays
                ? day.slice(0, 1)
                : smallCalendar && isMobile
                  ? day.slice(0, 1)
                  : day}
            </div>
          ))}
        </div>

        <div
          className={`grid grid-cols-7 ${fontSizeBase ? "gap-1.5" : "gap-1"}`}
        >
          {days.map((day) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isWeekend = disableWeekends && getDay(day) === 0;
            const isDisabled =
              isWeekend ||
              (minSelectableDate && isBefore(day, minSelectableDate)) ||
              (maxSelectableDate && isAfter(day, maxSelectableDate));

            return (
              <button
                key={day.toString()}
                onClick={() => !isDisabled && handleDateSelect(day)}
                disabled={isDisabled}
                className={`rounded-md ${fontSizeBase ? "text-2xs" : "text-xs"} leading-5 py-1 transition duration-300
                ${!isCurrentMonth ? "text-gray-400" : ""}
                ${isSelected ? "bg-primaryBlue text-white" : ""}
                ${isDisabled ? "text-gray-300 cursor-not-allowed" : ""}
                ${!isSelected && isCurrentMonth && !isDisabled ? "hover:bg-[#1A51AA24] hover:text-primaryBlue" : ""}`}
              >
                {format(day, "d")}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderMonthView = () => (
    <div className="grid grid-cols-3 gap-2 mt-2">
      {months.map((m) => {
        const date = new Date(year, m);
        const disabled =
          isBefore(endOfMonth(date), minSelectableDate) ||
          isAfter(startOfMonth(date), maxSelectableDate);

        return (
          <button
            key={m}
            onClick={() =>
              (!disabled && setView("day")) || setCurrentDate(date)
            }
            disabled={disabled}
            className={`py-2 rounded-md transition
            ${disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-[#1A51AA24] hover:text-primaryBlue"}`}
          >
            {format(date, "MMM")}
          </button>
        );
      })}
    </div>
  );

  const renderYearView = () => (
    <div className="grid grid-cols-3 gap-2 mt-2">
      {years.map((y) => {
        const yearStart = new Date(y, 0, 1);
        const yearEnd = new Date(y, 11, 31);
        const disabled =
          isBefore(yearEnd, minSelectableDate) ||
          isAfter(yearStart, maxSelectableDate);

        return (
          <button
            key={y}
            onClick={() =>
              !disabled && (setCurrentDate(new Date(y, 0)), setView("month"))
            }
            disabled={disabled}
            className={`py-2 rounded-md transition
            ${disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-[#1A51AA24] hover:text-primaryBlue"}`}
          >
            {y}
          </button>
        );
      })}
    </div>
  );

  const prevDisabled =
    view === "day" &&
    isBefore(subMonths(currentDate, 1), startOfMonth(minSelectableDate));
  const nextDisabled =
    view === "day" &&
    isAfter(addMonths(currentDate, 1), startOfMonth(maxSelectableDate));

  return (
    <div className="w-full mx-auto text-2xs font-medium">
      <div className="flex items-center justify-between mb-1 px-1">
        <div
          className={`${fontSizeBase ? "text-base" : "text-sm"} font-semibold text-primaryBlack flex gap-2`}
        >
          <button onClick={() => setView("month")}>
            {format(currentDate, "MMMM")}
          </button>
          <button onClick={() => setView("year")} className="text-[#20202080]">
            {format(currentDate, "yyyy")}
          </button>
        </div>

        <div className="flex ml-auto gap-1">
          <button
            onClick={handlePrev}
            disabled={prevDisabled}
            className={`${fontSizeBase && "p-1"} rounded-full transition-colors
              ${prevDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-gray-200"}`}
          >
            <icon.boxLeftSlidIcon />
          </button>
          <button
            onClick={handleNext}
            disabled={nextDisabled}
            className={`${fontSizeBase && "p-1"} rounded-full transition-colors
              ${nextDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-gray-200"}`}
          >
            <icon.boxLeftSlidIcon className="rotate-180" />
          </button>
        </div>
      </div>

      {view === "day" && renderDayView()}
      {view === "month" && renderMonthView()}
      {view === "year" && renderYearView()}
    </div>
  );
}
